import { logger } from "@/utils/logger";
import { supabase } from '@/integrations/supabase/client';
import { widgetDiagBeacon } from '@/utils/widgetDiagnostics';



export interface DealerConfig {
  dealer_id: number;
  domain: string;
  openai_key: string; // This will always be empty for security - keys are handled server-side only
  openai_assistant_id: string;
  google_places_key: string; // This will always be empty for security - keys are handled server-side only
  tradeboost: boolean;
  prompt_menu_is_active: boolean;
  prompt_id?: number; // System prompt ID (for OpenAI assistant)
  prompt_menu_id?: number; // Quick prompt menu ID (separate from system prompt)
  popular_questions_enabled: boolean;
  trade_banner_enabled: boolean;
  payments: boolean;
  payment_frequency?: 'monthly' | 'biweekly' | 'weekly';
  dealer_name?: string;
  welcome_message_en?: string;
  welcome_message_fr?: string;
  auto_expand_chat?: number;
  use_responses_api?: boolean;
  stream?: boolean;
  sales_pages_only?: boolean;
  certification_name?: string;
  vector_store_id?: string;
  inventory_load?: boolean;
  service_form_mode?: 'Site' | 'Chat';
  service_form_url_en?: string;
  service_form_url_fr?: string;
  waiting_video?: boolean;
  videos_enabled?: boolean;
  use_message_routing?: boolean;
  speech_to_speech?: boolean;
  // Branding data bundled from dealer-auth to avoid extra REST calls
  header_bg_col?: string;
  header_text_col?: string;
  corner_radius?: number;
  chat_collapsed_icon_bg?: string;
  chat_collapsed_icon_text?: string;
  
}

export interface DealerAuthRequest {
  dealer_id?: number;
  domain?: string;
  origin?: string;
  testMode?: boolean; // Allow loading disabled dealers when chatbottest=true
}

/**
 * Service for authenticating dealers with the backend
 */
export class DealerAuthService {
  /**
   * Authenticate dealer and get configuration
   */
  static async authenticate(request: DealerAuthRequest): Promise<DealerConfig> {
    const beaconDomain = request.domain ?? (typeof window !== 'undefined' ? window.location.hostname : null);
    widgetDiagBeacon('dealer-auth-requested', { dealer_domain: beaconDomain });
    try {
      const response = await supabase.functions.invoke('dealer-auth', {
        body: request,
      });

      // Raw response logging removed for security
      const { data, error } = response;

      if (error) {
        console.error('❌ DealerAuthService: Supabase function error:', error);
        console.error('❌ DealerAuthService: Error type:', typeof error);
        console.error('❌ DealerAuthService: Error message:', error.message);
        widgetDiagBeacon('dealer-auth-failed', {
          dealer_domain: beaconDomain,
          error_message: error.message || 'invoke_error',
        });
        
        // Check for specific unauthorized domain errors
        if (error.message?.includes('Dealer not found') || error.message?.includes('Domain mismatch')) {
          console.error('🚫 DealerAuthService: Detected unauthorized domain in error message');
          throw new Error('UNAUTHORIZED_DOMAIN: This domain is not authorized to use this application');
        }
        throw new Error(error.message || 'Authentication failed');
      }


      // Check for unauthorized domain first (404 responses put error details in data)
      if (data?.code === 'UNAUTHORIZED_DOMAIN' || data?.error?.includes('Dealer not found')) {
        widgetDiagBeacon('dealer-auth-failed', {
          dealer_domain: beaconDomain,
          error_message: 'UNAUTHORIZED_DOMAIN',
        });
        throw new Error('UNAUTHORIZED_DOMAIN: This domain is not authorized to use this application');
      }

      if (!data || !data.success || !data.dealer) {
        console.error('❌ DealerAuthService: Invalid response structure');
        console.error('❌ DealerAuthService: Full data:', JSON.stringify(data, null, 2));
        widgetDiagBeacon('dealer-auth-failed', {
          dealer_domain: beaconDomain,
          error_message: 'invalid_response',
        });
        throw new Error('UNAUTHORIZED_DOMAIN: This domain is not authorized to use this application');
      }

      widgetDiagBeacon('dealer-auth-ok', {
        dealer_domain: beaconDomain,
        extra: { dealer_id: data.dealer.dealer_id },
      });

      // Warm the serviceFormUrls localStorage cache from the d2cchatbot cookie
      // so Prompt Menu → Service L3 buttons resolve to the external URL on first click,
      // even before any AI service-form message has been rendered.
      try {
        const { storeServiceFormUrls } = await import('@/utils/cookieUtils');
        storeServiceFormUrls();
      } catch (e) {
        logger.warn('storeServiceFormUrls warmup failed:', e);
      }

      return data.dealer;
    } catch (error) {
      console.error('❌ DealerAuthService: Authentication failed');
      console.error('❌ DealerAuthService: Error type:', typeof error);
      console.error('❌ DealerAuthService: Error details:', error);
      
      if (error instanceof Error) {
        console.error('❌ DealerAuthService: Error message:', error.message);
        console.error('❌ DealerAuthService: Error stack:', error.stack);
        throw new Error(`Authentication failed: ${error.message}`);
      }
      
      throw new Error(`Authentication failed: Unknown error`);
    }
  }

  /**
   * Validate if dealer exists (without sensitive data)
   */
  static async validateDealer(dealer_id?: number, domain?: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.functions.invoke('dealer-auth', {
        body: { dealer_id, domain },
        method: 'GET'
      });

      if (error) {
        console.error('Dealer validation error:', error);
        return false;
      }

      return data?.exists || false;
    } catch (error) {
      console.error('Dealer validation failed:', error);
      return false;
    }
  }

}